import { useCallback, useMemo } from "react";
import type * as Y from "yjs";
import {
  deleteRow as domainDeleteRow,
  deleteSection as domainDeleteSection,
  listPanels,
  listRows,
  listSections,
  newId,
  newRow,
  newSection,
  panelRowId as toPanelRowId,
  sectionId as toSectionId,
  type Panel,
  type PanelMaps,
  type PanelRow,
  type PanelRowId,
  type Revision,
  type RowLoad,
  type Section,
  type SectionId,
  computeLoadTotals,
  patchPanel,
  patchRow,
  patchSection,
  putRow,
  putSection,
  runLocal,
  suggestFuse,
  suggestedFusePatch,
} from "@electro-planner/domain";

export type PanelMutations = {
  patchPanelMeta: (patch: Partial<Omit<Panel, "id" | "createdAt">>) => void;
  addRow: (init?: Partial<PanelRow>) => PanelRowId;
  insertRowAfter: (afterRowId: PanelRowId) => PanelRowId;
  insertRowsAfter: (afterRowId: PanelRowId | null, count: number) => PanelRowId[];
  updateRow: (id: PanelRowId, patch: Partial<Omit<PanelRow, "id">>) => void;
  removeRow: (id: PanelRowId) => void;
  removeRows: (ids: ReadonlyArray<PanelRowId>) => void;
  reorderRows: (orderedIds: ReadonlyArray<PanelRowId>) => void;
  duplicateRow: (id: PanelRowId) => PanelRowId | null;
  addSection: (code?: string, label?: string) => SectionId;
  patchSectionMeta: (id: SectionId, patch: Partial<Omit<Section, "id">>) => void;
  removeSection: (id: SectionId) => void;
  duplicateSection: (id: SectionId) => SectionId | null;
  moveRowToSection: (rowId: PanelRowId, sectionId: SectionId | null) => void;
  addRowToSection: (sectionId: SectionId | null) => PanelRowId;
  /** Flytta en rad till en viss DIN-skena. null = koppla bort från alla skenor. */
  setRowRail: (rowId: PanelRowId, railNumber: number | null) => void;
  /** Ändra indent-nivå med delta (-1/+1). Klamrat till [0, 3]. */
  indentRow: (id: PanelRowId, delta: 1 | -1) => void;
  /** Skapa revisions-snapshot av panelens nuvarande state. */
  takeRevisionSnapshot: (label: string, note?: string) => void;
  /**
   * Sätt laster-listan för en rad. Om `fuseAuto` är sant på raden uppdateras
   * ampRating/characteristic/component/loadWatts via `suggestFuse`.
   */
  setRowLoads: (rowId: PanelRowId, loads: ReadonlyArray<RowLoad>) => void;
  /**
   * Slå på/av auto-säkring för en rad. När aktiveras körs förslaget direkt
   * så användaren ser effekten omedelbart. När avaktiveras behålls de valda
   * värdena (användaren kan nu redigera manuellt).
   */
  setRowFuseAuto: (rowId: PanelRowId, fuseAuto: boolean) => void;
};

/**
 * Returnerar en stabil mutations-objekt som wrappar alla skrivningar i runLocal
 * (LOCAL_ORIGIN) så undo-manager och remote-awareness kan skilja lokala
 * operationer från inkommande CRDT-updates.
 */
export function useDomainMutations(
  doc: Y.Doc,
  maps: PanelMaps,
): PanelMutations {
  const currentPanelId = useCallback(() => {
    const panels = listPanels(maps);
    return panels[0]?.id;
  }, [maps]);

  const patchPanelMeta = useCallback(
    (patch: Partial<Omit<Panel, "id" | "createdAt">>) => {
      const pid = currentPanelId();
      if (!pid) return;
      runLocal(doc, () => {
        patchPanel(maps, pid, patch);
      });
    },
    [doc, maps, currentPanelId],
  );

  const addRow = useCallback(
    (init: Partial<PanelRow> = {}) => {
      const rows = listRows(maps);
      const maxOrder = rows.reduce((a, r) => Math.max(a, r.order), -1);
      const designation = init.designation ?? nextDesignation(rows);
      const row: PanelRow = {
        ...newRow(maxOrder + 1, designation),
        ...init,
        order: maxOrder + 1,
        designation,
      };
      runLocal(doc, () => {
        putRow(maps, row);
      });
      return row.id;
    },
    [doc, maps],
  );

  const insertRowAfter = useCallback(
    (afterRowId: PanelRowId) => {
      const rows = listRows(maps);
      const idx = rows.findIndex((r) => r.id === afterRowId);
      const newOrder = idx >= 0 ? rows[idx]!.order + 0.5 : (rows.at(-1)?.order ?? -1) + 1;
      const row: PanelRow = {
        ...newRow(newOrder, nextDesignation(rows)),
      };
      runLocal(doc, () => {
        putRow(maps, row);
        // Normalisera ordernycklar så de blir heltal igen.
        const all = listRows(maps).sort((a, b) => a.order - b.order);
        all.forEach((r, i) => {
          if (r.order !== i) patchRow(maps, r.id, { order: i });
        });
      });
      return row.id;
    },
    [doc, maps],
  );

  const insertRowsAfter = useCallback(
    (afterRowId: PanelRowId | null, count: number): PanelRowId[] => {
      if (count <= 0) return [];
      const rows = listRows(maps);
      const idx = afterRowId ? rows.findIndex((r) => r.id === afterRowId) : -1;
      const baseOrder =
        idx >= 0 ? rows[idx]!.order : (rows.at(-1)?.order ?? -1);
      const sectionId = idx >= 0 ? rows[idx]!.sectionId : undefined;
      const created: PanelRow[] = [];
      let step = 1;
      // Steg genererar order-nycklar mellan basrad och efterföljande rad.
      const nextRow = idx >= 0 ? rows[idx + 1] : undefined;
      const slot = nextRow ? (nextRow.order - baseOrder) / (count + 1) : 1;
      for (let i = 0; i < count; i++) {
        const r: PanelRow = {
          ...newRow(baseOrder + slot * step, nextDesignation([...rows, ...created])),
          sectionId,
        };
        created.push(r);
        step++;
      }
      runLocal(doc, () => {
        for (const r of created) putRow(maps, r);
        const all = listRows(maps).sort((a, b) => a.order - b.order);
        all.forEach((r, i) => {
          if (r.order !== i) patchRow(maps, r.id, { order: i });
        });
      });
      return created.map((r) => r.id);
    },
    [doc, maps],
  );

  const updateRow = useCallback(
    (id: PanelRowId, patch: Partial<Omit<PanelRow, "id">>) => {
      runLocal(doc, () => {
        patchRow(maps, id, patch);
      });
    },
    [doc, maps],
  );

  const removeRow = useCallback(
    (id: PanelRowId) => {
      runLocal(doc, () => {
        domainDeleteRow(maps, id);
      });
    },
    [doc, maps],
  );

  const removeRows = useCallback(
    (ids: ReadonlyArray<PanelRowId>) => {
      if (ids.length === 0) return;
      runLocal(doc, () => {
        for (const id of ids) domainDeleteRow(maps, id);
      });
    },
    [doc, maps],
  );

  const duplicateRow = useCallback(
    (id: PanelRowId): PanelRowId | null => {
      const rows = listRows(maps);
      const src = rows.find((r) => r.id === id);
      if (!src) return null;
      const idx = rows.findIndex((r) => r.id === id);
      const next = rows[idx + 1];
      const newOrder = next ? (src.order + next.order) / 2 : src.order + 1;
      const copy: PanelRow = {
        ...src,
        id: toPanelRowId(newId("row")),
        order: newOrder,
        designation: nextDesignation(rows),
        reviewed: false, // duplikat startar alltid som ogranskad
      };
      runLocal(doc, () => {
        putRow(maps, copy);
        const all = listRows(maps).sort((a, b) => a.order - b.order);
        all.forEach((r, i) => {
          if (r.order !== i) patchRow(maps, r.id, { order: i });
        });
      });
      return copy.id;
    },
    [doc, maps],
  );

  const reorderRows = useCallback(
    (orderedIds: ReadonlyArray<PanelRowId>) => {
      runLocal(doc, () => {
        orderedIds.forEach((id, i) => {
          patchRow(maps, id, { order: i });
        });
      });
    },
    [doc, maps],
  );

  const addSection = useCallback(
    (code?: string, label?: string) => {
      const sections = listSections(maps);
      const autoCode = code ?? `S${sections.length + 1}`;
      const section: Section = newSection(
        autoCode,
        sections.length,
        label,
      );
      runLocal(doc, () => {
        putSection(maps, section);
      });
      return section.id;
    },
    [doc, maps],
  );

  const patchSectionMeta = useCallback(
    (id: SectionId, patch: Partial<Omit<Section, "id">>) => {
      runLocal(doc, () => {
        patchSection(maps, id, patch);
      });
    },
    [doc, maps],
  );

  const removeSection = useCallback(
    (id: SectionId) => {
      runLocal(doc, () => {
        domainDeleteSection(maps, id);
      });
    },
    [doc, maps],
  );

  const duplicateSection = useCallback(
    (id: SectionId): SectionId | null => {
      const sections = listSections(maps);
      const src = sections.find((s) => s.id === id);
      if (!src) return null;
      const rows = listRows(maps).filter((r) => r.sectionId === id);
      const maxOrder = sections.reduce((a, s) => Math.max(a, s.order), -1);
      const newSectId = toSectionId(newId("sect"));
      const newCode = nextFreeSectionCode(sections, src.code);
      const newLabel = src.label ? `Kopia av ${src.label}` : undefined;
      const maxRowOrder = listRows(maps).reduce(
        (a, r) => Math.max(a, r.order),
        -1,
      );
      const newRows: PanelRow[] = rows.map((r, i) => ({
        ...r,
        id: toPanelRowId(newId("row")),
        sectionId: newSectId,
        order: maxRowOrder + 1 + i,
        reviewed: false,
      }));
      runLocal(doc, () => {
        putSection(maps, {
          id: newSectId,
          code: newCode,
          label: newLabel,
          order: maxOrder + 1,
        });
        for (const r of newRows) putRow(maps, r);
      });
      return newSectId;
    },
    [doc, maps],
  );

  const moveRowToSection = useCallback(
    (rowId: PanelRowId, sectionId: SectionId | null) => {
      runLocal(doc, () => {
        patchRow(maps, rowId, { sectionId: sectionId ?? undefined });
      });
    },
    [doc, maps],
  );

  const addRowToSection = useCallback(
    (sectionId: SectionId | null) => {
      const rows = listRows(maps);
      const maxOrder = rows.reduce((a, r) => Math.max(a, r.order), -1);
      const designation = nextDesignation(rows);
      const row: PanelRow = {
        ...newRow(maxOrder + 1, designation),
        sectionId: sectionId ?? undefined,
      };
      runLocal(doc, () => {
        putRow(maps, row);
      });
      return row.id;
    },
    [doc, maps],
  );

  const setRowRail = useCallback(
    (rowId: PanelRowId, railNumber: number | null) => {
      runLocal(doc, () => {
        patchRow(maps, rowId, { railNumber: railNumber ?? undefined });
      });
    },
    [doc, maps],
  );

  const indentRow = useCallback(
    (id: PanelRowId, delta: 1 | -1) => {
      const rows = listRows(maps);
      const src = rows.find((r) => r.id === id);
      if (!src) return;
      const current = src.indent ?? 0;
      const next = Math.max(0, Math.min(3, current + delta));
      if (next === current) return;
      runLocal(doc, () => {
        patchRow(maps, id, { indent: next === 0 ? undefined : next });
      });
    },
    [doc, maps],
  );

  const setRowLoads = useCallback(
    (rowId: PanelRowId, loads: ReadonlyArray<RowLoad>) => {
      const rows = listRows(maps);
      const row = rows.find((r) => r.id === rowId);
      if (!row) return;
      const patch: Partial<Omit<PanelRow, "id">> = {
        loads: loads.length > 0 ? loads : undefined,
      };
      const polesIn: 1 | 3 = row.poles === 3 || row.poles === 4 ? 3 : 1;
      if (row.fuseAuto && loads.length > 0) {
        const suggestion = suggestFuse(loads, { poles: polesIn });
        if (suggestion) Object.assign(patch, suggestedFusePatch(suggestion));
      } else {
        // Spegla bara totaleffekt i loadWatts även utan auto-säkring —
        // statusraden och skena-varningarna använder loadWatts.
        const totals = computeLoadTotals(loads, { poles: polesIn });
        patch.loadWatts = loads.length > 0 ? Math.round(totals.watts) : undefined;
      }
      runLocal(doc, () => {
        patchRow(maps, rowId, patch);
      });
    },
    [doc, maps],
  );

  const setRowFuseAuto = useCallback(
    (rowId: PanelRowId, fuseAuto: boolean) => {
      const rows = listRows(maps);
      const row = rows.find((r) => r.id === rowId);
      if (!row) return;
      runLocal(doc, () => {
        patchRow(maps, rowId, { fuseAuto: fuseAuto || undefined });
        if (fuseAuto && row.loads && row.loads.length > 0) {
          const polesIn: 1 | 3 = row.poles === 3 || row.poles === 4 ? 3 : 1;
          const suggestion = suggestFuse(row.loads, { poles: polesIn });
          if (suggestion) patchRow(maps, rowId, suggestedFusePatch(suggestion));
        }
      });
    },
    [doc, maps],
  );

  const takeRevisionSnapshot = useCallback(
    (label: string, note?: string) => {
      const pid = currentPanelId();
      if (!pid) return;
      const panels = listPanels(maps);
      const panel = panels[0];
      if (!panel) return;
      // Snapshot = panel + sections + rows minus revisioner (för att undvika
      // exponentiell storlek över tid).
      const snapshotPayload = {
        panel: { ...panel, revisions: undefined },
        sections: listSections(maps),
        rows: listRows(maps),
      };
      const rev: Revision = {
        id: newId("rev"),
        createdAt: Date.now(),
        label: label.trim() || `Rev ${String.fromCharCode(65 + (panel.revisions?.length ?? 0))}`,
        ...(note && note.trim() ? { note: note.trim() } : {}),
        snapshot: JSON.stringify(snapshotPayload),
      };
      const prev = panel.revisions ?? [];
      runLocal(doc, () => {
        patchPanel(maps, pid, { revisions: [...prev, rev] });
      });
    },
    [doc, maps, currentPanelId],
  );

  return useMemo(
    () => ({
      patchPanelMeta,
      addRow,
      insertRowAfter,
      insertRowsAfter,
      updateRow,
      removeRow,
      removeRows,
      reorderRows,
      duplicateRow,
      addSection,
      patchSectionMeta,
      removeSection,
      duplicateSection,
      moveRowToSection,
      addRowToSection,
      setRowRail,
      indentRow,
      takeRevisionSnapshot,
      setRowLoads,
      setRowFuseAuto,
    }),
    [
      patchPanelMeta,
      addRow,
      insertRowAfter,
      insertRowsAfter,
      updateRow,
      removeRow,
      removeRows,
      reorderRows,
      duplicateRow,
      addSection,
      patchSectionMeta,
      removeSection,
      duplicateSection,
      moveRowToSection,
      addRowToSection,
      setRowRail,
      indentRow,
      takeRevisionSnapshot,
      setRowLoads,
      setRowFuseAuto,
    ],
  );
}

/**
 * Hittar nästa lediga S-kod med samma prefix som källan (S1 → S4 om S1-S3
 * redan finns). Fallback till S-N där N är totalt antal sektioner + 1.
 */
function nextFreeSectionCode(
  sections: ReadonlyArray<Section>,
  sourceCode: string,
): string {
  const prefix = sourceCode.replace(/\d+$/, "") || "S";
  const taken = new Set(sections.map((s) => s.code));
  for (let i = 1; i < 100; i++) {
    const candidate = `${prefix}${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${prefix}${sections.length + 1}`;
}

/**
 * Föreslår nästa F-beteckning (F1, F2, F3 …) baserat på befintliga rader.
 * Hoppar över manuellt satta icke-F-koder.
 */
function nextDesignation(rows: ReadonlyArray<PanelRow>): string {
  const fNumbers = rows
    .map((r) => /^F(\d+)$/i.exec(r.designation)?.[1])
    .filter((s): s is string => !!s)
    .map((s) => parseInt(s, 10));
  const max = fNumbers.length > 0 ? Math.max(...fNumbers) : 0;
  return `F${max + 1}`;
}
